import { useQuery } from '@tanstack/react-query'
import { useTRPC } from '../lib/trpc'
import { Notice } from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import { PublicSchedule } from './PublicSchedule'

/**
 * A congregation's schedule, no login.
 *
 * The schedule is `PublicSchedule`, a reader's layout over the same resolved
 * occasion the office screen and the printed sheet render, so the two cannot
 * show different times. The chrome around it is `PublicShell`, which belongs to
 * the site rather than to this page. What is left here is only this page's own
 * question: which org.
 *
 * Which org is decided by the route map and handed in, so this page parses no
 * URLs of its own. Everything called here is a `publicProcedure` — no member
 * data is drawn before auth.
 */
export function PublicZmanimPage({
  orgId,
  onLogin,
}: {
  /** The org whose schedule this is, from the URL's first segment. */
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const orgs = useQuery(trpc.org.list.queryOptions())
  // The masthead picker offers somewhere else to go, so it offers what the
  // directory lists. The lookup above cannot use the same read: every org
  // answers at its own address, including the ones a directory leaves out.
  const listed = useQuery(trpc.org.directory.queryOptions())

  // Matched on the org's own public id, and never on position in the list:
  // falling back to the first org would serve one arbitrary congregation's
  // schedule to everybody who reached the platform root.
  const shul = orgs.data?.find((s) => s.shortId === orgId)

  if (orgs.isLoading || !shul) {
    return (
      <PublicShell name={null} orgId={null} current="zmanim" onLogin={onLogin}>
        {orgs.isLoading ? <Notice>Loading…</Notice> : <PublicNotFound />}
      </PublicShell>
    )
  }

  return (
    <PublicShell
      name={shul.name}
      nameHe={shul.nameHe}
      language={shul.publicLanguage}
      orgId={shul.shortId}
      acceptsDonations={shul.acceptsDonations}
      siblings={listed.data}
      contact={shul}
      current="zmanim"
      onLogin={onLogin}
    >
      <PublicSchedule shul={shul} />
    </PublicShell>
  )
}
